/**
 * Vue publique d'un projet partagé — `/partage/:token`, HORS `AppShell` (pas de session,
 * pas de navigation applicative) : c'est le seul rendu avec `LoginPage` à vivre en dehors.
 *
 * L'appel ne porte jamais de jeton (`{ auth: false }`, D5, § 5 de la spec CIAM) : un jeton
 * expiré resté dans `sessionStorage` ne doit jamais rendre cette page muette ou en erreur.
 */

import { useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { api } from '../lib/api.js';
import { useAsync } from '../hooks/useAsync.js';
import MenuLangue from '../components/common/MenuLangue.jsx';
import { BRAND, BRAND_GLYPH, EYEBROW, TD, TH } from '../ui/theme.js';
import GlypheExiPlan from '../components/common/GlypheExiPlan.jsx';
import {
  Empty,
  PageHeader,
  Progress,
  StatusTag,
  labelForKind,
  taskReference,
} from '../components/common.jsx';

export function SharedProjectPage() {
  const { t } = useTranslation();
  const { token } = useParams();
  const { data, error, loading } = useAsync(
    (signal) => api.get(`/public/shares/${token}`, { signal, auth: false }),
    [token],
  );

  if (loading) {
    return (
      <Shell>
        <p className="p-6 text-sm text-ink-soft">{t('sharedProject.loading')}</p>
      </Shell>
    );
  }

  if (error) {
    const notFound = error.status === 404;
    return (
      <Shell>
        <div className="p-6">
          <Empty>{notFound ? t('sharedProject.notFound') : t('sharedProject.loadError')}</Empty>
        </div>
      </Shell>
    );
  }

  const { project, tasks = [], dependencies = [] } = data.data;
  const milestones = tasks.filter((task) => task.kind === 'milestone');

  return (
    <Shell>
      <div
        role="status"
        className="border-b-2 border-accent-trait bg-accent-wash px-3 py-1.5 text-center font-mono text-[11px] uppercase tracking-eyebrow text-accent-ink"
      >
        {t('sharedProject.readOnlyBanner')}
      </div>

      {/* Même en-tête que les pages de l'application — trame `DECO_ENTETE` comprise (règle de
          cohérence 1.3.0 d'`@exidis/ui` : tout en-tête de page la porte). */}
      <PageHeader
        bounded={false}
        eyebrow={project.key}
        title={project.name}
        actions={<StatusTag status={project.status} />}
      />

      <div className="p-4">
        <section className="mt-2">
          <p className={EYEBROW}>{t('sharedProject.milestones')}</p>
          {milestones.length === 0 ? (
            <p className="mt-2 text-sm text-ink-soft">{t('sharedProject.noMilestones')}</p>
          ) : (
            <ul className="mt-2 flex flex-col gap-2">
              {milestones.map((milestone) => (
                <li
                  key={milestone.id}
                  className="flex items-center justify-between gap-2 border-b-2 border-line-soft py-2"
                >
                  <span className="text-sm text-ink">{milestone.title}</span>
                  <span className="font-mono text-[11px] text-ink-faint">
                    {milestone.dueDate ?? '—'}
                  </span>
                </li>
              ))}
            </ul>
          )}
        </section>

        <section className="mt-6">
          <p className={EYEBROW}>{t('sharedProject.tasks')}</p>
          {tasks.length === 0 ? (
            <div className="mt-2">
              <Empty>{t('sharedProject.noTasks')}</Empty>
            </div>
          ) : (
            <table className="mt-2 w-full border-collapse">
              <thead>
                <tr>
                  <th className={TH}>{t('sharedProject.columns.reference')}</th>
                  <th className={TH}>{t('sharedProject.columns.title')}</th>
                  <th className={TH}>{t('sharedProject.columns.kind')}</th>
                  <th className={TH}>{t('sharedProject.columns.status')}</th>
                  <th className={TH}>{t('sharedProject.columns.progress')}</th>
                </tr>
              </thead>
              <tbody>
                {tasks.map((task) => (
                  <tr key={task.id}>
                    <td className={`${TD} font-mono`}>{taskReference(project.key, task.number)}</td>
                    <td className={TD}>{task.title}</td>
                    <td className={TD}>{labelForKind(task.kind, t)}</td>
                    <td className={TD}>
                      <StatusTag status={task.status} />
                    </td>
                    <td className={TD}>
                      <Progress
                        value={task.progress ?? 0}
                        label={t('sharedProject.progressLabel', { title: task.title })}
                      />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </section>

        {dependencies.length > 0 && (
          <p className="mt-4 text-[11px] text-ink-faint">
            {t('sharedProject.dependencyCount', { count: dependencies.length })}
          </p>
        )}
      </div>
    </Shell>
  );
}

function Shell({ children }) {
  return (
    <div className="min-h-screen bg-paper">
      <header className="h-11 border-b-2 border-t-2 border-line flex items-center justify-between px-3">
        <span className={BRAND}>
          <span className={BRAND_GLYPH} aria-hidden="true">
            <GlypheExiPlan />
          </span>
          exiPlan
        </span>
        <MenuLangue />
      </header>
      {children}
    </div>
  );
}
